/**
 * Logs a message to the console if the logger is enabled.
 *
 * @module console
 * @param message
 * @param objects
 * @public
 * @returns {void}
 */
export const log = (message, ...objects) => {

	if (!isLoggerEnabled()) return

	if (typeof message === "object") {
		console.log("Pixel Manager: ", message, ...objects)
	} else if (objects.length) {
		console.log("Pixel Manager: " + message, ...objects)
	} else {
		console.log("Pixel Manager: " + message)
	}
}

/**
 * Logs an error message to the console.
 *
 * @module console
 * @param message
 * @param objects
 * @public
 * @returns {void}
 */
export const error = (message, ...objects) => {

	// if (!isLoggerEnabled()) return

	// if message is an object, log the keys
	if (typeof message === "object") {
		console.error("Pixel Manager: ", message, ...objects)
	} else if (objects.length) {
		console.error("Pixel Manager: " + message, ...objects)
	} else {
		console.error("Pixel Manager: " + message)
	}
}

/**
 * Logs a warning message to the console.
 *
 * @module console
 * @param message
 * @param objects
 * @public
 * @returns {void}
 */
export const warn = (message, ...objects) => {

	if (typeof message === "object") {
		console.warn("Pixel Manager: ", message, ...objects)
	} else if (objects.length) {
		console.warn("Pixel Manager: " + message, ...objects)
	} else {
		console.warn("Pixel Manager: " + message)
	}
}

/**
 * Whether a URL parameter is present.
 *
 * Deliberately not pmw.urlHasParameter(): every log call runs through here,
 * including calls from the modules that load before functions.js is even
 * required, and a logger that throws turns a message into a broken page. The
 * check is two lines, so it is kept local rather than borrowed from a part of
 * the library that may not exist yet.
 *
 * @since 1.67.1
 * @param {string} parameter
 * @return {boolean}
 */
const urlHasParameter = parameter => {

	try {
		return new URLSearchParams(window.location.search).has(parameter)
	} catch (e) {
		return false
	}
}

/**
 * Remember the logger choice for the session, if the session store is up.
 *
 * Same reasoning as urlHasParameter(): pmw.storeData lives in functions.js and
 * is missing for every log call that happens before it loads. Not being able
 * to remember the choice is harmless, throwing is not.
 *
 * @since 1.67.1
 * @param {boolean} enabled
 * @return {void}
 */
const rememberLoggerChoice = enabled => {

	if (typeof pmw.storeData !== "function") return

	try {
		pmw.storeData("loggerEnabled", enabled)
	} catch (e) {
		// A blocked or full session store must not break a log call.
	}
}

/**
 * Checks if the logger is enabled.
 *
 * @return {boolean}
 */
const isLoggerEnabled = () => {

	if (urlLoggerOff()) return false

	if (sessionLoggerEnabled()) return true

	if (urlLoggerOn()) return true

	return !!pmwDataLayer?.general?.logger?.is_active
}

/**
 * Turn off the logger if the URL parameter is set.
 *
 * @return {boolean}
 */
const urlLoggerOff = () => {

	if (!urlHasParameter("pmwloggeroff")) return false

	rememberLoggerChoice(false)

	return true
}

/**
 * Check if the logger is enabled for the session.
 *
 * @return {boolean}
 */
const sessionLoggerEnabled = () => {

	if (typeof pmw.retrieveData !== "function") return false

	try {
		return pmw.retrieveData("loggerEnabled")
	} catch (e) {
		return false
	}
}

/**
 * Turn on the logger if the URL parameter is set.
 *
 * @return {boolean}
 */
const urlLoggerOn = () => {

	if (!urlHasParameter("pmwloggeron")) return false

	rememberLoggerChoice(true)

	return true
}
